import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, FileText } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABEL = {
  quotation: "Quotation",
  estimation: "Estimation",
  proforma: "Proforma",
  invoice: "Invoice",
  sales_order: "Sales Order",
};

/**
 * Batch C — full-page read-only viewer for billing documents (quotation /
 * estimation / proforma / invoice / sales_order). Used by `Open in New Tab`.
 * Read-only — no edit/delete actions. Billing calculations untouched.
 */
export default function BillingDocPage({ user, logout }) {
  const { docType, docId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError("");
    // Use the authenticated api client (JWT Bearer via interceptor) — same auth
    // the Billing list uses. Raw axios omitted the token → 401 on this route.
    api.get(`/billing/${docId}`)
      .then((r) => { if (alive) setDoc(r.data); })
      .catch((e) => {
        if (!alive) return;
        const status = e?.response?.status;
        if (status === 401) setError("Your session has expired. Please log in again.");
        else if (status === 403) setError("You do not have access to this document.");
        else if (status === 404) setError("Document not found.");
        else setError(e?.response?.data?.detail || "Failed to load document.");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [docId]);

  const close = () => navigate("/billing");
  const label = TYPE_LABEL[(docType || "").toLowerCase()] || "Document";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="billing-doc-page">
      <div className="border-b border-slate-200 bg-white px-6 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={close} className="inline-flex items-center gap-1 text-sm text-slate-700 hover:text-[#002FA7]" data-testid="billing-doc-back">
          <ArrowLeft size={16} weight="bold" /> Back to Billing
        </button>
        <div className="text-xs text-slate-400">/ {label} / {docId.slice(0, 10)}</div>
      </div>
      {loading && <div className="p-10 text-center text-slate-500" data-testid="billing-doc-loading">Loading…</div>}
      {error && (
        <div className="p-10 text-center" data-testid="billing-doc-error">
          <div className="text-rose-700 font-bold mb-2">{error}</div>
          <button onClick={close} className="text-sm text-[#002FA7] underline">← Back to Billing</button>
        </div>
      )}
      {doc && !error && (
        <div className="max-w-4xl mx-auto p-6 space-y-4">
          <div className="bg-white border border-slate-200 p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
                <h1 className="text-2xl font-bold mt-1 flex items-center gap-2"><FileText size={22} className="text-[#002FA7]" />{doc.doc_number || doc.doc_id}</h1>
                <div className="text-sm text-slate-600 mt-1">Customer: {doc.customer_name || "—"}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Total</div>
                <div className="text-2xl font-bold text-slate-800">₹{Number(doc.total || 0).toLocaleString("en-IN")}</div>
                {doc.status && <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{doc.status}</div>}
              </div>
            </div>
          </div>

          {Array.isArray(doc.line_items) && doc.line_items.length > 0 && (
            <div className="bg-white border border-slate-200 overflow-hidden" data-testid="billing-doc-lines">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left p-3">Item</th>
                    <th className="text-right p-3 w-20">Qty</th>
                    <th className="text-right p-3 w-28">Rate</th>
                    <th className="text-right p-3 w-28">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {doc.line_items.map((li, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="p-3 text-slate-700">{li.product_name || li.description || "—"}</td>
                      <td className="p-3 text-right">{li.quantity || 0}</td>
                      <td className="p-3 text-right">₹{Number(li.rate || 0).toLocaleString("en-IN")}</td>
                      <td className="p-3 text-right font-medium">₹{Number(li.amount || (li.quantity || 0) * (li.rate || 0)).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card label="Subtotal" value={`₹${Number(doc.subtotal || 0).toLocaleString("en-IN")}`} />
            <Card label="Discount" value={`₹${Number(doc.total_discount || 0).toLocaleString("en-IN")}`} />
            <Card label="GST" value={`₹${Number(doc.total_tax || 0).toLocaleString("en-IN")}`} />
            <Card label="Grand Total" value={`₹${Number(doc.total || 0).toLocaleString("en-IN")}`} />
          </div>

          <div className="text-[11px] text-slate-400">
            Created: {(doc.created_at || "").slice(0, 19).replace("T", " ")}
            {doc.updated_at && <> · Updated: {(doc.updated_at || "").slice(0, 19).replace("T", " ")}</>}
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-bold text-slate-800">{value}</div>
    </div>
  );
}
